/**
 * The three public API calls the checkout makes. No cookies (`credentials:
 * 'omit'`), no caching, and no API key: everything here is reachable by any
 * customer holding the link, which is why the API exposes so little on them.
 */

// `??`, not `||`: an empty VITE_API_URL means "same origin", i.e. the dev and
// preview proxy, and must not fall back to anything.
const API_BASE: string = import.meta.env.VITE_API_URL ?? '';

export interface LinkInfo {
  slug: string;
  amountFiat?: string | null;
  fiatCurrency: string;
  description?: string | null;
  isActive: boolean;
  isExpired: boolean;
  testMode?: boolean;
  checkoutApiVersion?: number;
}

export interface CheckoutTarget {
  mode: 'embed' | 'redirect';
  url: string;
  expiresAt?: string;
}

export interface PayResult {
  transactionId?: string;
  checkoutSession?: { token: string; expiresAt: string | null };
  redirectUrl?: string;
  providerId?: string;
  checkout?: CheckoutTarget | null;
}

export type SessionStatus = 'awaiting_payment' | 'processing' | 'succeeded' | 'failed' | 'cancelled' | 'expired' | 'refunded';

export interface CheckoutSession {
  status: SessionStatus;
  final: boolean;
  transactionId: string;
  returnUrl: string | null;
  testMode?: boolean;
}

export type ApiResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; code?: string; message?: string; retryAfterSec: number | null };

function retryAfter(res: Response): number | null {
  const raw = res.headers.get('Retry-After');
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...init, credentials: 'omit', cache: 'no-store' });
  } catch {
    return { ok: false, status: 0, retryAfterSec: null };
  }
  let body: { success?: boolean; data?: T; error?: { code?: string; message?: string } } | null = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (res.ok && body && body.success !== false && body.data !== undefined) {
    return { ok: true, status: res.status, data: body.data };
  }
  return {
    ok: false,
    status: res.ok ? 502 : res.status,
    code: body?.error?.code,
    message: body?.error?.message,
    retryAfterSec: retryAfter(res),
  };
}

export function getLinkInfo(slug: string): Promise<ApiResult<LinkInfo>> {
  return call<LinkInfo>(`/api/v1/payment-links/${encodeURIComponent(slug)}`);
}

export function startPayment(
  slug: string,
  body: { paymentMethod: 'credit_card'; customerCountry: string }
): Promise<ApiResult<PayResult>> {
  return call<PayResult>(`/api/v1/payment-links/${encodeURIComponent(slug)}/pay`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function getCheckoutSession(token: string): Promise<ApiResult<CheckoutSession>> {
  return call<CheckoutSession>(`/api/v1/checkout/sessions/${encodeURIComponent(token)}`);
}
